import { Link } from 'react-router-dom'
import { HERO_CONTENT, IMAGES } from '../data/content'

function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-16 px-4 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 -z-20 bg-gradient-to-b from-white via-white to-brand-blue-light/20"></div>

      {/* Decorative Cloud-like Background Blobs */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Top right cloud blob */}
        <svg
          className="absolute -top-20 -right-32 w-[900px] h-[700px] text-brand-blue-light opacity-35"
          viewBox="0 0 200 150"
          fill="currentColor"
        >
          <ellipse cx="100" cy="80" rx="90" ry="60" />
          <ellipse cx="60" cy="90" rx="50" ry="40" />
          <ellipse cx="140" cy="85" rx="55" ry="45" />
          <ellipse cx="100" cy="60" rx="70" ry="45" />
        </svg>

        {/* Bottom left cloud blob */}
        <svg
          className="absolute -bottom-20 -left-40 w-[600px] h-[400px] text-brand-blue opacity-20"
          viewBox="0 0 200 150"
          fill="currentColor"
        >
          <ellipse cx="100" cy="80" rx="85" ry="55" />
          <ellipse cx="50" cy="90" rx="45" ry="35" />
          <ellipse cx="150" cy="85" rx="50" ry="40" />
          <ellipse cx="100" cy="55" rx="60" ry="40" />
        </svg>

        {/* Bottom right cloud blob */}
        <svg
          className="absolute -bottom-10 right-0 w-[500px] h-[350px] text-brand-blue opacity-25"
          viewBox="0 0 200 150"
          fill="currentColor"
        >
          <ellipse cx="100" cy="85" rx="80" ry="50" />
          <ellipse cx="55" cy="95" rx="40" ry="30" />
          <ellipse cx="145" cy="90" rx="45" ry="35" />
          <ellipse cx="100" cy="65" rx="55" ry="35" />
        </svg>

        {/* Mid-right decorative cloud */}
        <svg
          className="absolute top-1/4 -right-20 w-[400px] h-[300px] text-brand-blue-light opacity-40"
          viewBox="0 0 200 150"
          fill="currentColor"
        >
          <ellipse cx="100" cy="80" rx="75" ry="50" />
          <ellipse cx="55" cy="85" rx="40" ry="30" />
          <ellipse cx="140" cy="82" rx="45" ry="35" />
        </svg>
      </div>

      {/* Decorative Icons */}
      {/* Circle outline - left side */}
      <div className="absolute left-20 top-1/2 hidden lg:block">
        <div className="w-20 h-20 rounded-full border-2 border-brand-blue opacity-50"></div>
      </div>

      {/* Small filled dot - left top */}
      <div className="absolute left-14 top-1/3 hidden lg:block">
        <div className="w-3 h-3 rounded-full bg-brand-blue opacity-60"></div>
      </div>

      {/* Plus sign - left bottom */}
      <div className="absolute left-10 bottom-1/3 hidden lg:block">
        <svg className="w-10 h-10 text-brand-blue opacity-60" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
      </div>

      {/* X mark - right top */}
      <div className="absolute right-24 top-28 hidden lg:block">
        <svg className="w-12 h-12 text-brand-blue opacity-40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </div>

      {/* X mark - bottom right */}
      <div className="absolute right-1/4 bottom-1/4 hidden lg:block">
        <svg className="w-8 h-8 text-brand-blue opacity-50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </div>

      {/* Small dot - right middle */}
      <div className="absolute right-1/3 top-1/3 hidden lg:block">
        <div className="w-4 h-4 rounded-full bg-brand-blue opacity-40"></div>
      </div>

      {/* Content - Wider Layout */}
      <div className="max-w-[1500px] mx-auto w-full grid lg:grid-cols-5 gap-12 items-center px-6">
        {/* Text Content - Takes more space */}
        <div className="lg:col-span-3 text-center lg:text-left">
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-semibold leading-[1.15] mb-8 text-black animate-fade-in-up">
            <span className="block whitespace-nowrap">Striving for <span className="text-brand-blue">Equity</span> and</span>
            <span className="block whitespace-nowrap"><span className="text-brand-blue">Access</span> in Computer</span>
            <span className="block">Science Education</span>
          </h1>
          <p className="text-xl md:text-2xl text-black font-medium mb-8 leading-relaxed max-w-3xl animate-fade-in-up-delay-1">
            Dedicated to making computer science a more ubiquitous field,{' '}
            <span className="text-brand-blue">CS foreach</span> is a student organization at UC San Diego that strives to make access to{' '}
            <span className="text-brand-blue">CS</span> opportunities and resources more equitable{' '}
            <span className="text-brand-blue underline">for each</span> and every student.
          </p>
          <div className="flex flex-wrap gap-5 justify-center lg:justify-start animate-fade-in-up-delay-2">
            <Link
              to={HERO_CONTENT.primaryButton.href}
              className="btn btn-primary-solid rounded-lg"
            >
              {HERO_CONTENT.primaryButton.label}
            </Link>
            <Link
              to={HERO_CONTENT.secondaryButton.href}
              className="btn btn-outline rounded-lg"
            >
              {HERO_CONTENT.secondaryButton.label}
            </Link>
          </div>
        </div>

        {/* Image with Cloud Background - Takes less space */}
        <div className="lg:col-span-2 hidden lg:flex justify-center items-center relative">
          {/* Cloud blob behind image */}
          <svg
            className="absolute w-[500px] h-[400px] text-brand-blue-light opacity-50 -z-10"
            viewBox="0 0 200 150"
            fill="currentColor"
          >
            <ellipse cx="100" cy="80" rx="90" ry="60" />
            <ellipse cx="50" cy="90" rx="50" ry="40" />
            <ellipse cx="150" cy="85" rx="55" ry="45" />
            <ellipse cx="100" cy="55" rx="65" ry="45" />
          </svg>

          {/* Circular Image Container */}
          <div className="relative w-80 h-80 rounded-full overflow-hidden border-4 border-white shadow-2xl">
            <img
              src={IMAGES.about}
              alt="Students learning computer science"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Decorative ring */}
          <div className="absolute w-96 h-96 rounded-full border-2 border-brand-blue opacity-30"></div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
